import React, { FC, useState, useMemo, useRef } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import { Button, DatePicker } from 'tea-component';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import CascadeProjectCustom from '@sg/components/CascadeProjectCustom';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import { LiveTitlesTab, createBtnPoint, SGBuriedBtnPoint } from '@sg/utils/buriedPoint';
import LiveTitlesOverview from './component/LiveTitlesOverview';
import EditNotesModal from './component/EditNotesModal';
import { useLiveVisualizationAll } from './hooks/useLiveVisualizationAll';
import type {
  TOverviewType,
  ILiveVisualizationAllProps,
} from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/type';
import Abnormal from '@home/components/Abnormal';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { getCascadeProjectDatas, getCascadeProjectValues } from '@gp/utils/project';
import { TYPES } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/const';
import RoutePrompt from '@sg/components/RoutePrompt';
import { getButtonPermission } from '@sg/_config/permission';
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import './index.scss';
import { useDeepCompareEffect } from 'ahooks';
import { useLiveVisualizationAllAutoSave } from './hooks/useLiveVisualizationAllAutoSave';

/**
 * LiveVisualizationAll — the ALL (aggregated) Live Titles view.
 * Shown when user selects "ALL" in the studio/project selector (studio_id=all).
 *
 * "所有页面都需要创建模拟数据，以展示设计稿上的效果".
 */
const LiveVisualizationAllPage: FC<ILiveVisualizationAllProps> = (props) => {
  const { studio, studioList } = props;
  const [isEditing, setIsEditing] = useState(false);
  // Edit Notes 弹窗状态（模拟数据，待接入真实数据源）
  const [isEditNotesVisible, setIsEditNotesVisible] = useState(false);
  const notesBeforeEditModalRef = useRef<any[] | null>(null);

  // Studio/Project 二级级联配置
  const cascadeInfoList = useMemo(
    () => [
      { level: 1, key: 'studio_id', nameKey: 'studio_name', title: 'Studio', isEndPanel: false },
      { level: 2, key: 'project_id', nameKey: 'game_title', title: 'Project', isEndPanel: true },
    ],
    [],
  );

  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();
  const { MonthPicker } = DatePicker;

  const editable = useMemo(
    () => getButtonPermission('studio_liveTitles_liveVisualizationAll'),
    [permissionReady],
  );

  const {
    projects,
    notes,
    loading,
    loFilterLoading,
    handleActiveOverview,
    handleDateFilterChange,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleConfirm,
    studioProjectList,
    summaryDataMC,
    summaryDataLO,
    isError,
    activeOverview,
    setActiveOverview,
    selectedMonth,
    setSelectedMonth,
    cascadeValue,
    setCascadeValue,
    changeFilterProjects,
    fetchLODataByFilter,
    setNotes,
    setProjects,
    projectOrderList,
    setProjectOrderList,
  } = useLiveVisualizationAll();

  const {
    autoSaveVisible,
    hasUnsavedChanges,
    captureBaseline,
    handleEditWithAutoSave,
    handleAutoSaveConfirm,
    handleAutoSaveCancel,
    clearAutoSave,
  } = useLiveVisualizationAllAutoSave({
    isEditing,
    projects,
    notes,
    projectOrderList,
    setProjects,
    setNotes,
    setProjectOrderList,
  });

  const handleDateChange = (value: any) => {
    setSelectedMonth(value);
    handleDateFilterChange?.(value);
  };

  const handleEditNotesClose = () => {
    if (notesBeforeEditModalRef.current) {
      setNotes(notesBeforeEditModalRef.current);
    }
    notesBeforeEditModalRef.current = null;
    setIsEditNotesVisible(false);
  };

  const handleEditNotesConfirm = () => {
    notesBeforeEditModalRef.current = null;
    setIsEditNotesVisible(false);
  };

  // handleEditNotes 打开EditNotesModal
  const handleEditNotes = () => {
    notesBeforeEditModalRef.current = JSON.parse(JSON.stringify(notes || []));
    setIsEditNotesVisible(true);
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      return;
    }
    handleEditWithAutoSave(() => {
      const btnPoint = createBtnPoint(LiveTitlesTab, 'liveVisualizationAllEdit', 'Edit LiveTitlesAll');
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
      // Enter edit mode: capture baseline for RoutePrompt + auto-save comparison
      captureBaseline(projects || [], notes || [], projectOrderList || []);
      setIsEditing(true);
    });
  };

  const handleSave = async () => {
    await handleConfirm?.();
    clearAutoSave();
    setIsEditing(false);
  };

  const handleCancel = () => {
    handleProjectsReorderCancel?.();
    clearAutoSave();
    setIsEditing(false);
  };

  // 将 studioProjectList 转为级联树结构
  const cascadeTreeOptions = useMemo(() => {
    if (!studioProjectList || studioProjectList.length === 0) {
      return [];
    }
    // 转换 projects → child 以适配 getCascadeProjectDatas
    // studioProjectList 已由 getFormatProjectDatas 添加了 project_id
    const transformed = studioProjectList.map((studio: any) => ({
      studio_id: studio.studio_id,
      studio_name: studio.studio,
      child: (studio.projects || []).map((p: any) => ({
        game_title: p.game_title,
        cover: p.cover,
        project_id: p.project_id,
      })),
    }));
    return getCascadeProjectDatas(transformed, '', cascadeInfoList);
  }, [studioProjectList, cascadeInfoList]);

  // 计算所有最后节点的 project_id，修改getCascadeProjectValues方法
  const allCascadeValues = useMemo(() => {
    return getCascadeProjectValues(cascadeTreeOptions, false);
  }, [cascadeTreeOptions]);

  // Derive the single selected studio name: non-empty only when exactly one
  // studio's projects are selected (regardless of how many projects within it).
  const singleSelectedStudioName = useMemo(() => {
    if (!cascadeValue || cascadeValue.length === 0 || cascadeValue.includes('all')) {
      return '';
    }
    // When all projects are selected (default state), treat as "no filter active"
    if (allCascadeValues.length > 0 && cascadeValue.length >= allCascadeValues.length) {
      return '';
    }
    // Build project_id → studio name lookup from studioProjectList
    const projectToStudio = new Map<string, string>();
    studioProjectList.forEach((studio: any) => {
      const name = studio.studio || '';
      (studio.projects || []).forEach((p: any) => {
        if (p.project_id) projectToStudio.set(p.project_id, name);
      });
    });
    const studioNames = new Set<string>();
    cascadeValue.forEach(v => {
      const name = projectToStudio.get(v);
      if (name) studioNames.add(name);
    });
    return studioNames.size === 1 ? [...studioNames][0] : '';
  }, [cascadeValue, studioProjectList, allCascadeValues]);

  useDeepCompareEffect(() => {
    setCascadeValue(allCascadeValues);
  }, [allCascadeValues]);

  const handleCascadeChange = (values: string[]) => {
    setCascadeValue(values);
    setActiveOverview(TYPES.lo as TOverviewType);
    // Re-fetch LO summary card data with the filtered game_titles from backend,
    // then update the project list. MC card is unaffected by the filter.
    fetchLODataByFilter(values, studioProjectList, selectedMonth);
  };

  return (
    <div className="dbs-live-visualization-all dbs-home-studio-card-studio">
      <div className="dbs-studio-filter">
        <div className="dbs-segment-row">
          <div className="dbs-segment-row__left">
            <DatePickerWrapper className="dbs-month-picker-with-tooltip" label={formatMessage({ id: 'dbs_month' })}>
              <MonthPicker
                overlayClassName="dbs-month-picker-overlay"
                value={selectedMonth}
                onChange={handleDateChange}
                placeholder="Please Select"
                disabled={isEditing}
              />
            </DatePickerWrapper>
            <CascadeProjectCustom
              options={cascadeTreeOptions}
              value={cascadeValue}
              onChange={handleCascadeChange}
              allValues={allCascadeValues}
              btnTitle="Studio/Project"
              disabled={isEditing || loFilterLoading}
              cascadeInfoList={cascadeInfoList}
            />
            {editable && isEditing && (
              <Button
                onClick={(e) => {
                  e?.stopPropagation(); // Prevent collapse event
                  handleEditNotes?.();
                }}
                style={{ height: '36px', minWidth: '80px' }}
                className="dbs-outline-btn"
                disabled={loading}
              >
                <SvgIcon type={'pencil'} className="prefix-icon" />
                {formatMessage({ id: 'dbs_edit_notes' })}
              </Button>
            )}
          </div>
          <div className="dbs-segment-row__right">
            {editable && !isEditing && (
              <Button
                onClick={(e) => {
                  e?.stopPropagation(); // Prevent collapse event
                  handleEditToggle();
                }}
                className="dbs-gradient-btn"
                disabled={loading}
              >
                {formatMessage({ id: 'dbs_edit' })}
              </Button>
            )}
            {editable && isEditing && (
              <div className="dbs-rich-text-buttons">
                <Button
                  onClick={(e) => {
                    e?.stopPropagation(); // Prevent collapse event
                    handleSave?.();
                  }}
                  style={{ marginRight: '10px' }}
                  className="dbs-gradient-btn"
                  disabled={loading}
                >
                  {formatMessage({ id: 'dbs_confirm' })}
                </Button>
                <Button
                  onClick={(e) => {
                    e?.stopPropagation(); // Prevent collapse event
                    handleCancel();
                  }}
                  style={{ height: '36px', minWidth: '80px' }}
                  className="dbs-outline-btn"
                  disabled={loading}
                >
                  {formatMessage({ id: 'dbs_cancel' })}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Abnormal
        isLoading={loading}
        isError={isError}
        loadingMsg="Loading"
        component={
          <LiveTitlesOverview
            studio={studio}
            studioList={studioList}
            isEditing={isEditing}
            activeOverview={activeOverview}
            handleActiveOverview={handleActiveOverview}
            handleProjectsReorder={handleProjectsReorder}
            handleProjectsReorderCancel={handleProjectsReorderCancel}
            summaryDataMC={summaryDataMC}
            summaryDataLO={summaryDataLO}
            projects={projects}
            selectedMonth={selectedMonth}
            singleSelectedStudioName={singleSelectedStudioName}
            loFilterLoading={loFilterLoading}
          />
        }
      />

      {/* Edit Notes Modal */}
      <EditNotesModal
        visible={isEditNotesVisible}
        onClose={handleEditNotesClose}
        onConfirm={handleEditNotesConfirm}
        month={selectedMonth?.format?.('YYYY-MM') || ''}
        notes={notes}
        setNotes={setNotes}
      />

      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={() => {
          handleAutoSaveConfirm();
          setIsEditing(true);
        }}
        onClose={() => {
          handleAutoSaveCancel();
          captureBaseline(projects || [], notes || [], projectOrderList || []);
          setIsEditing(true);
        }}
      />

      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          await handleSave();
        }}
      />
    </div>
  );
};

export default LiveVisualizationAllPage;
